import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { LoadingGrid, SimpleAlert } from '../../../../../rbts/helpers';
import { useObtenerNiveles, useObtenerSubsecciones } from '../hooks/EventosBoleteraHook';
import { NivelEntradaModel } from '../models/NivelEntradaModel';
import { SubSeccionModel } from '../models/SubSeccionModel';
import { eliminarSubSeccion } from '../../../../service/boletera/EventosBoletera';
import { ModalSubSeccion } from '../_modals/ModalSubSeccion';
import { ModalGenerarAsientos } from '../_modals/ModalGenerarAsientos';
import { ModalListaAsientos } from '../_modals/ModalListaAsientos';

type Props = { eventoId: number };

const SubseccionesAsientos: React.FC<Props> = ({ eventoId }) => {
  const { resumen: nivelesData } = useObtenerNiveles(eventoId, 0);
  const nivelesNumerados = nivelesData.rows.filter((n: NivelEntradaModel) => n.modo_venta === 'numerado');

  const [nivelId, setNivelId] = useState<number | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (nivelesNumerados.length > 0 && nivelId === null) {
      setNivelId(nivelesNumerados[0].id);
    }
  }, [nivelesData.rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const { resumen, loading } = useObtenerSubsecciones(nivelId, reload);

  const [showModalSub, setShowModalSub] = useState(false);
  const [subSeleccionada, setSubSeleccionada] = useState<SubSeccionModel | null>(null);
  const [showModalAsientos, setShowModalAsientos] = useState(false);
  const [subParaAsientos, setSubParaAsientos] = useState<SubSeccionModel | null>(null);
  const [showModalLista, setShowModalLista] = useState(false);
  const [subParaLista, setSubParaLista] = useState<SubSeccionModel | null>(null);

  const abrirAgregar = () => { setSubSeleccionada(null); setShowModalSub(true); };
  const abrirEditar = (s: SubSeccionModel) => { setSubSeleccionada(s); setShowModalSub(true); };
  const abrirGenerar = (s: SubSeccionModel) => { setSubParaAsientos(s); setShowModalAsientos(true); };
  const abrirLista = (s: SubSeccionModel) => { setSubParaLista(s); setShowModalLista(true); };

  const confirmarEliminar = (s: SubSeccionModel) => {
    Swal.fire({
      title: `¿Eliminar sub-sección "${s.nombre}"?`,
      text: 'También se eliminarán sus asientos. Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Eliminar',
      reverseButtons: true,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-light me-3' },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await eliminarSubSeccion(nivelId!, s.id);
          setReload((r) => r + 1);
        } catch (error: any) {
          SimpleAlert(error?.response?.data?.doc?.message || 'Error al eliminar.', 3000, 'error');
        }
      }
    });
  };

  return (
    <div>
      {/* ── Selector de nivel numerado ── */}
      <div className="mb-5">
        <label className="fw-bold fs-7 text-muted text-uppercase mb-2 d-block">Nivel numerado</label>
        {nivelesNumerados.length === 0 ? (
          <div className="text-muted fs-7">
            No hay niveles en modo "Numerado". Créalos en la pestaña "Niveles de entrada".
          </div>
        ) : (
          <select
            className="form-select"
            style={{ maxWidth: 280 }}
            value={nivelId || ''}
            onChange={(e) => { setNivelId(Number(e.target.value)); setReload(0); }}
          >
            {nivelesNumerados.map((n: NivelEntradaModel) => (
              <option key={n.id} value={n.id}>{n.nombre}</option>
            ))}
          </select>
        )}
      </div>

      {nivelId && (
        <>
          {/* ── Botón agregar ── */}
          <div className="d-flex justify-content-end mb-4">
            <button className="btn btn-primary btn-sm" onClick={abrirAgregar}>
              + Agregar sub-sección
            </button>
          </div>

          {/* ── Tabla ── */}
          {loading ? (
            <LoadingGrid text="Cargando sub-secciones..." />
          ) : resumen.rows.length === 0 ? (
            <div className="text-center text-muted py-10">
              No hay sub-secciones. Agrégalas con el botón de arriba.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bolder text-muted">
                    <th>SUB-SECCIÓN</th>
                    <th>CANT. BOLETOS</th>
                    <th>VENDIDOS</th>
                    <th>ASIENTOS</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.rows.map((s) => {
                    const asientosLabel = s.asiento_count && s.asiento_count > 0
                      ? `${s.asiento_count.toLocaleString()}${s.num_filas && s.asientos_por_fila ? ` (${s.num_filas}×${s.asientos_por_fila})` : ''}`
                      : '—';
                    return (
                      <tr key={s.id}>
                        <td className="fw-bold">{s.nombre}</td>
                        <td>{Number(s.cantidad_boletos).toLocaleString()}</td>
                        <td>{(s.vendidos ?? 0).toLocaleString()}</td>
                        <td>
                          {asientosLabel === '—' ? (
                            <span className="text-muted me-4">{asientosLabel}</span>
                          ) : (
                            <button
                              className="btn btn-sm btn-link p-0 me-4 fs-7"
                              onClick={() => abrirLista(s)}
                            >
                              {asientosLabel}
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-link p-0 fs-7"
                            disabled={(s.vendidos ?? 0) > 0}
                            title={(s.vendidos ?? 0) > 0 ? 'No se puede regenerar: hay boletos vendidos' : undefined}
                            onClick={() => abrirGenerar(s)}
                          >
                            Regenerar asientos
                          </button>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-icon btn-sm btn-light-primary me-1"
                            title="Editar"
                            onClick={() => abrirEditar(s)}
                          >
                            <i className="bi bi-pencil fs-6" />
                          </button>
                          {(s.vendidos ?? 0) > 0 ? (
                            <span className="badge badge-light-secondary">Con ventas</span>
                          ) : (
                            <button
                              className="btn btn-icon btn-sm btn-light-danger"
                              title="Eliminar"
                              onClick={() => confirmarEliminar(s)}
                            >
                              <i className="bi bi-trash fs-6" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="text-muted fs-7 mt-6">
        La venta por asiento individual no está activa en esta etapa — esto solo deja preparada la estructura
        para cuando se active (ver Fase 5A, sección 2.1).
      </div>

      <ModalSubSeccion
        show={showModalSub}
        nivelId={nivelId || 0}
        subseccion={subSeleccionada}
        aforoDisponible={resumen.disponible}
        handleClose={(refresh) => {
          setShowModalSub(false);
          setSubSeleccionada(null);
          if (refresh) setReload((r) => r + 1);
        }}
      />

      <ModalGenerarAsientos
        show={showModalAsientos}
        subseccion={subParaAsientos}
        handleClose={(refresh) => {
          setShowModalAsientos(false);
          setSubParaAsientos(null);
          if (refresh) setReload((r) => r + 1);
        }}
      />

      <ModalListaAsientos
        show={showModalLista}
        subseccion={subParaLista}
        handleClose={() => { setShowModalLista(false); setSubParaLista(null); }}
      />
    </div>
  );
};

export { SubseccionesAsientos };
